import strings from '../../../strings/strings.json';
import { breakpoints, sizes } from 'theme';
import { BodySmall, Card, FlexColMd, FlexColSm, FlexMdAlignCenter, FlexSpaceBetween } from 'components/Elements';
import styled from 'styled-components';
import Button from 'components/buttons/Button';
import ImageIcon from 'assets/icons/pathway/ImageIcon';
import VideoIcon from 'assets/icons/pathway/VideoIcon';
import AudioIcon from 'assets/icons/pathway/AudioIcon';
import { DOCUMENTS_URL } from 'lib/_api-helpers';
import { CreateFileSource as FileSource } from 'models/inputModels/CreateTaskRequest';
import VideoPlayer from 'pages/learner/components/VideoPlayer';
import AudioPlayer from 'pages/learner/components/AudioPlayer';
import { useElementWidth } from 'hooks/useElementWidth';

type FileCardProps = {
    fileName?: string;
    removeFile: () => void;
    disabled: boolean;
    removeFileButtonRef: React.RefObject<HTMLButtonElement>;
    children?: React.ReactNode;
};

interface FileCardWrapperProps extends FileCardProps {
    filePath: string;
    source: FileSource;
}

interface ImageFileCardWrapperProps extends FileCardWrapperProps {
    imageDescription: string;
    children?: React.ReactNode;
}

type InnerFileCardProps = FileCardProps & {
    fileIcon: JSX.Element;
    removeFileButtonTitle: string;
    removeFileButtonAria: string;
};

export function ImageCard({
    fileName,
    removeFile,
    disabled,
    filePath,
    imageDescription,
    source,
    removeFileButtonRef,
    children,
}: ImageFileCardWrapperProps): JSX.Element {
    return (
        <FileCard
            fileName={fileName}
            removeFile={removeFile}
            disabled={disabled}
            fileIcon={<ImageIcon />}
            removeFileButtonTitle={strings.selectFileForm.removeImageButton}
            removeFileButtonAria={strings.selectFileForm.removeImageButtonAria}
            removeFileButtonRef={removeFileButtonRef}
        >
            <Image
                src={source === 'local' ? `${filePath}` : `${DOCUMENTS_URL}/${filePath}`}
                alt={imageDescription ?? strings.taskPage.imageAltTextPlaceholder}
            />
            {children}
        </FileCard>
    );
}

export function VideoCard({
    fileName,
    removeFile,
    disabled,
    filePath,
    source,
    removeFileButtonRef,
}: FileCardWrapperProps): JSX.Element {
    return (
        <FileCard
            fileName={fileName}
            removeFile={removeFile}
            disabled={disabled}
            fileIcon={<VideoIcon />}
            removeFileButtonTitle={strings.selectFileForm.removeVideoButton}
            removeFileButtonAria={strings.selectFileForm.removeVideoButtonAria}
            removeFileButtonRef={removeFileButtonRef}
        >
            <VideoPlayer filepath={filePath} fileSource={source} />
        </FileCard>
    );
}

export function AudioCard({
    fileName,
    removeFile,
    disabled,
    filePath,
    source,
    removeFileButtonRef,
}: FileCardWrapperProps): JSX.Element {
    return (
        <FileCard
            fileName={fileName}
            removeFile={removeFile}
            disabled={disabled}
            fileIcon={<AudioIcon />}
            removeFileButtonTitle={strings.selectFileForm.audio.removeAudioButton}
            removeFileButtonAria={strings.selectFileForm.audio.removeAudioButtonAria}
            removeFileButtonRef={removeFileButtonRef}
        >
            <AudioPlayer filepath={filePath} fileSource={source} />
        </FileCard>
    );
}

const Image = styled.img`
    object-fit: contain;
    width: 100%;
    height: 350px;
`;

function FileCard({
    fileName,
    removeFile,
    disabled,
    fileIcon,
    removeFileButtonTitle,
    removeFileButtonAria,
    removeFileButtonRef,
    children,
}: InnerFileCardProps): JSX.Element {
    const [ref, width] = useElementWidth<HTMLDivElement>();

    if (width === undefined) {
        return <Card ref={ref} />;
    }

    const isNarrow = width <= parseInt(breakpoints.xs);

    return (
        <Card ref={ref}>
            <FlexColSm>
                <FlexColMd>
                    {fileName && (
                        <FileInfoRow $columnLayout={isNarrow}>
                            <FlexMdAlignCenter style={{ width: '100%' }}>
                                <FileIconContainer>{fileIcon}</FileIconContainer>
                                <BodySmall $bold>{fileName}</BodySmall>
                            </FlexMdAlignCenter>
                        </FileInfoRow>
                    )}
                    {children}
                </FlexColMd>
                <Button
                    ref={removeFileButtonRef}
                    title={removeFileButtonTitle}
                    aria={removeFileButtonAria}
                    colour={'BLACK'}
                    onClick={removeFile}
                    disabled={disabled}
                />
            </FlexColSm>
        </Card>
    );
}

const FileIconContainer = styled.div`
    margin-top: ${sizes.spacingSm};
`;

const FileInfoRow = styled(FlexSpaceBetween)<{ $columnLayout?: boolean }>`
    flex-direction: ${({ $columnLayout }) => ($columnLayout ? 'column' : 'row')};
    gap: ${({ $columnLayout }) => ($columnLayout ? sizes.spacingSm : '0')};
    width: 100%;
`;
